// ---------------------------------------------------------------------------
// PWA Caisse Tablette — Caisse Screen (main POS view)
// Split layout: ProductGrid (left) + CartSidebar (right)
// ---------------------------------------------------------------------------

import { useState } from 'react';
import ProductGrid from '@/components/ProductGrid';
import CartSidebar from '@/components/CartSidebar';
import { useCartStore } from '@/store/cart-store';
import { useAppStore } from '@/store/app-store';
import { useProducts } from '@/hooks/useProducts';
import type { Product } from '@/lib/types';

export default function CaisseScreen() {
  const { products, loading } = useProducts();
  const addProduct = useCartStore((s) => s.addProduct);
  const items = useCartStore((s) => s.items);
  const showToast = useAppStore((s) => s.showToast);
  const setScannerOpen = useAppStore((s) => s.setScannerOpen);

  const [showCart, setShowCart] = useState(false);

  // On tablet/desktop, cart is always visible (side-by-side).
  // On mobile, cart is toggled as an overlay.

  const handleProductClick = (product: Product) => {
    if (!product.is_active) {
      showToast('Produit non disponible', 'warning');
      return;
    }
    addProduct(product);
  };

  const handleScan = () => {
    setScannerOpen(true);
  };

  return (
    <div className="h-full flex">
      {/* Left: Product grid */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Toolbar */}
        <div className="flex items-center gap-2 px-4 py-2 shrink-0">
          <button
            type="button"
            onClick={handleScan}
            className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M23 6V1h-5" />
              <path d="M1 6V1h5" />
              <path d="M23 18v5h-5" />
              <path d="M1 18v5h5" />
              <line x1="1" y1="12" x2="23" y2="12" />
            </svg>
            Scanner
          </button>

          {/* Mobile cart toggle */}
          <button
            type="button"
            onClick={() => setShowCart(!showCart)}
            className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 md:hidden ml-auto relative"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="9" cy="21" r="1" />
              <circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
            </svg>
            Panier
            {items.length > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-accent text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {items.length}
              </span>
            )}
          </button>
        </div>

        {/* Loading state */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="animate-spin mr-2">
              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            </svg>
            Chargement des produits...
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            <ProductGrid
              products={products}
              onProductClick={handleProductClick}
            />
          </div>
        )}
      </div>

      {/* Right: Cart sidebar (always visible on md+, toggleable on mobile) */}
      <div
        className={`${
          showCart
            ? 'fixed inset-0 z-[100] bg-black/50 md:relative md:inset-auto md:z-auto md:bg-transparent'
            : 'hidden md:flex'
        } md:w-[380px] md:border-l md:border-white/10 md:flex-col`}
      >
        {/* Close overlay on mobile by tapping background */}
        {showCart && (
          <div
            className="absolute inset-0 md:hidden"
            onClick={() => setShowCart(false)}
          />
        )}

        <div
          className={`${
            showCart
              ? 'absolute right-0 top-0 bottom-0 w-[340px] animate-slide-up md:relative md:w-full md:animate-none'
              : ''
          } bg-surface h-full flex flex-col`}
        >
          <CartSidebar />
        </div>
      </div>
    </div>
  );
}
